import { z } from 'zod';
import { timestampSchema } from './common';
import { scheduleItemTypeSchema } from './scheduling';

/**
 * Schedule baselines: the plan somebody stood behind, and what has happened to
 * it since.
 *
 * Costs have had this from the start -- an approved budget is a sequence of
 * numbers somebody signed, and "we are within budget" is verifiable because the
 * reader can see the budget being raised. The schedule had nothing equivalent.
 * You could prove a cost overrun and not a delay, which is an odd asymmetry in
 * a governance tool, and it is why `docs/decisions.md` has listed baselines as
 * outstanding since the scheduling module was written.
 *
 * ## The shape, and what it deliberately is not
 *
 * A baseline is a **snapshot with a label**, not a document with a lifecycle.
 * `CostBudgetVersion` moves draft -> approved -> superseded because a budget is
 * authored: somebody types amounts, somebody else agrees to them, and the gap
 * between those two acts is where the draft lives. A baseline is not authored.
 * It is taken -- the schedule as it already stood at a moment -- so there is
 * nothing to draft and nobody to agree with. Two states, `active` and
 * `superseded`, and capturing a new one supersedes the old.
 *
 * `kind` is likewise absent where the cost model stores one: the first baseline
 * is the original and the rest are revisions, which `sequence === 1` already
 * says. Storing it would be a second copy of a fact that cannot disagree with
 * itself only because nothing ever writes it.
 *
 * ## Immutability
 *
 * A baseline that could be edited afterwards could not be reported against --
 * the same sentence `CostForecastVersion` carries, and the reason there is no
 * update route here and a database trigger refusing `UPDATE` on the captured
 * rows. Correcting a baseline means capturing another one, which leaves the
 * mistake visible instead of silently rewriting what was promised.
 *
 * ## Why the engine does not change
 *
 * Nothing in `@dashboard/scheduling` knows baselines exist. A baseline is a
 * record and a comparison, never an input: it must not pull a date, constrain a
 * float, or appear in the critical path. Keeping it outside the engine is what
 * makes "the plan" and "the plan we agreed" two readings of the same data
 * rather than two schedules that can drift apart.
 */

/* ------------------------------------------------------------- the record */

export const SCHEDULE_BASELINE_STATUSES = ['active', 'superseded'] as const;
export const scheduleBaselineStatusSchema = z.enum(SCHEDULE_BASELINE_STATUSES);
export type ScheduleBaselineStatus = (typeof SCHEDULE_BASELINE_STATUSES)[number];

export const scheduleBaselineSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Per project, from 1. Baseline 1 is the original; the rest are revisions. */
  sequence: z.number().int().positive(),
  label: z.string(),
  /** Why this was rebaselined. Absent on the original, where there is no why. */
  note: z.string().nullable(),
  status: scheduleBaselineStatusSchema,
  capturedByUserId: z.string().uuid().nullable(),
  capturedAt: timestampSchema,
  /** Leaf and summary rows captured. The denominator for every variance. */
  taskCount: z.number().int().nonnegative(),
  /**
   * The latest finish in the snapshot: what the project was promised to end on.
   * Null when nothing in the baseline had dates, which is possible and worth
   * seeing rather than hiding behind a zero.
   */
  plannedFinish: z.string().date().nullable(),
});
export type ScheduleBaseline = z.infer<typeof scheduleBaselineSchema>;

export const captureScheduleBaselineSchema = z
  .object({
    label: z.string().min(1).max(200),
    note: z.string().max(2000).nullish(),
  })
  .strict();
export type CaptureScheduleBaseline = z.infer<typeof captureScheduleBaselineSchema>;

/* ------------------------------------------------------------- the variance */

/**
 * How one task compares with what was baselined.
 *
 * Variance is signed working days, positive meaning later than planned. Signed
 * because a task can finish early, and reporting that as zero would make a
 * schedule that pulled forward look exactly like one that landed on plan. In
 * working days because that is the unit the whole engine plans in -- a variance
 * of "two days" that silently meant a weekend would be worse than no number.
 */
export const scheduleVarianceRowSchema = z.object({
  taskId: z.string().uuid(),
  name: z.string(),
  itemType: scheduleItemTypeSchema,
  hasChildren: z.boolean(),
  parentId: z.string().uuid().nullable(),

  baselineStart: z.string().date().nullable(),
  baselineFinish: z.string().date().nullable(),
  currentStart: z.string().date().nullable(),
  currentFinish: z.string().date().nullable(),

  /** Null when either side has no date to compare. */
  startVarianceDays: z.number().int().nullable(),
  finishVarianceDays: z.number().int().nullable(),
});
export type ScheduleVarianceRow = z.infer<typeof scheduleVarianceRowSchema>;

/**
 * A task the baseline has and the schedule no longer does.
 *
 * Kept as its own list rather than a row with null current dates, because
 * "removed from the plan" and "not yet scheduled" are different facts that
 * would be indistinguishable in a single table. Scope leaving a project is
 * exactly what a baseline exists to make visible.
 */
export const scheduleVarianceRemovedSchema = z.object({
  name: z.string(),
  baselineStart: z.string().date().nullable(),
  baselineFinish: z.string().date().nullable(),
});
export type ScheduleVarianceRemoved = z.infer<typeof scheduleVarianceRemovedSchema>;

export const scheduleVarianceSchema = z.object({
  projectId: z.string().uuid(),
  /** Null when the project has never been baselined; every list is then empty. */
  baseline: scheduleBaselineSchema.nullable(),

  /** Tasks present in both, with their variance. */
  compared: z.array(scheduleVarianceRowSchema),
  /** Tasks added since the baseline. Scope growth, with no variance to report. */
  added: z.array(scheduleVarianceRowSchema),
  /** Tasks the baseline held that the schedule no longer does. */
  removed: z.array(scheduleVarianceRemovedSchema),

  /**
   * The headline number: working days between the baselined finish and the
   * current one. This is "are we late", and it is the reason the whole feature
   * exists.
   */
  projectFinishVarianceDays: z.number().int().nullable(),
  baselineFinish: z.string().date().nullable(),
  currentFinish: z.string().date().nullable(),

  /**
   * Completion against what was *promised*, rather than against the current
   * plan (docs/schedule-progress-design.md section 7.3).
   *
   * The schedule page compares progress with the plan as it stands today, which
   * is the operational question -- am I doing this week what the plan says this
   * week. It is also gameable, and visibly so: push a task's dates out and it
   * returns to "on plan" without a brick being laid.
   *
   * This is the other comparison, in the place it belongs: the same progress,
   * measured against the curve the baselined dates imply. Where the two differ
   * is where the plan has moved rather than the work.
   *
   * All three are null when the project has no baseline or nobody has reported.
   */
  progressBasisPoints: z.number().int().min(0).max(10_000).nullable(),
  baselinePlannedBasisPoints: z.number().int().min(0).max(10_000).nullable(),
  /** Positive is behind, the sign every other variance here uses. */
  progressBehindByWorkingDays: z.number().int().nullable(),
});
export type ScheduleVariance = z.infer<typeof scheduleVarianceSchema>;

/**
 * Classifies a variance for display, so a list, a badge and a tile cannot
 * disagree about when a number is worth noticing.
 *
 * The threshold is deliberately not configurable. A per-deployment "how late is
 * late" setting is a number nobody sets and everybody inherits, and it would
 * make two projects' reports incomparable; ahead / on plan / behind is the
 * distinction people actually act on.
 */
export type VarianceTone = 'ahead' | 'on_plan' | 'behind';

export function varianceTone(days: number | null): VarianceTone {
  if (days === null || days === 0) return 'on_plan';
  return days > 0 ? 'behind' : 'ahead';
}
